import React, { Fragment, useContext, ReactElement, useState } from "react";
import { sortBy } from "lodash";
import { Link } from "wouter";
import { browserName } from "react-device-detect";
import { gql, useQuery } from "urql";
import useAxios from "axios-hooks";
import axios from "axios";
import { ChevronDown, LogOut, X } from "lucide-react";

import { useSideNav } from "../../../components/src/contexts/SideNavContext";
import { userHaveAccessTo } from "../common/Permissions";
import { loginContext, getDefaultLoginValue } from "./Login";
import { PermissionTypeEnum, PermissionAssetEnum } from "../types.generated";
import {
    REACT_APP_DB_NAME,
    REACT_APP_API_URI,
    REACT_APP_AUTH_URI,
    REACT_APP_NODEJOBAPI_URI,
    REACT_APP_GIT_HASH,
    VITE_DATE,
    REACT_APP_PYJOBAPI_URI
} from "../env";

interface IMenuItem {
    title: string;
    // without to, the title only expands and collapses the sub menu
    to?: string;
    absolute?: boolean;
    subMenu?: IMenuItem[];
}

interface IMenuLinkPropTypes {
    value: IMenuItem;
    className?: string;
    onClose: any;
    // a sub menu inside a sub menu
    nested?: boolean;
}

const topLinkClassName =
    "block flex-1 rounded-md px-3 py-2 text-[15px] font-medium !text-white no-underline transition-colors hover:bg-white/10 hover:no-underline";
const subLinkClassName =
    "block rounded-md px-3 py-1.5 text-sm !text-white/75 no-underline transition-colors hover:bg-white/10 hover:!text-white hover:no-underline";

const MenuLink = ({ value, onClose, nested }: IMenuLinkPropTypes): ReactElement => {
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const linkClassName = nested ? "flex-1 " + subLinkClassName : topLinkClassName;
    let topLink: ReactElement;
    if (typeof value.to === "undefined") {
        topLink = (
            <button
                type="button"
                onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
                className={linkClassName + " !border-0 bg-transparent text-left"}
            >
                {value.title}
            </button>
        );
    } else if (value.absolute) {
        topLink = (
            <a href={value.to} rel="noopener noreferrer" className={linkClassName}>
                {value.title}
            </a>
        );
    } else {
        topLink = (
            <Link to={value.to} onClick={onClose} className={linkClassName}>
                {value.title}
            </Link>
        );
    }

    if (!value.subMenu) {
        return <li>{topLink}</li>;
    }

    return (
        <li>
            <div className="flex items-center gap-1">
                {topLink}
                <button
                    type="button"
                    aria-label={isSubMenuOpen ? `Collapse ${value.title}` : `Expand ${value.title}`}
                    aria-expanded={isSubMenuOpen}
                    onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md !border-0 bg-transparent p-0 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isSubMenuOpen ? "rotate-180" : ""}`} />
                </button>
            </div>
            {isSubMenuOpen ? (
                <ul className="my-1 ml-5 space-y-0.5 border-l border-white/15 pl-2">
                    {value.subMenu.map((d, i) =>
                        d.subMenu ? (
                            <MenuLink key={i} value={d} onClose={onClose} nested />
                        ) : (
                            <li key={i}>
                                {d.absolute ? (
                                    <a href={d.to} rel="noopener noreferrer" className={subLinkClassName}>
                                        {d.title}
                                    </a>
                                ) : (
                                    <Link to={d.to} onClick={onClose} className={subLinkClassName}>
                                        {d.title}
                                    </Link>
                                )}
                            </li>
                        )
                    )}
                </ul>
            ) : null}
        </li>
    );
};

export const getMe = gql`
    query {
        me {
            _id
            name
            noteIds
            termsOfService {
                _id
                data
            }
            roles {
                _id
                assets
                clientIds
                permissionType
                name
            }
            frontendRole {
                assets
            }
        }

        funds: parties(filter: { typeIn: [Fund], withTransactions: true, withEnabledFundClasses: true }) {
            _id
            name
            longName
            types
        }

        clients: parties(filter: { typeIn: [Client], withTransactions: true }) {
            _id
            name
            longName
            types
        }
    }
`;

export const SideNav = (): ReactElement => {
    const [{ fetching, error, data }] = useQuery({ query: getMe });
    const { login, setLogin } = useContext(loginContext);
    const [__, request] = useAxios({ withCredentials: true }, { manual: true });
    const { isOpen, closeSideNav } = useSideNav();

    if (fetching) return <div></div>;
    if (error) return <div>{error.toString()}</div>;

    let menu = [];

    let clients = sortBy(
        data.funds
            .concat(data.clients)
            .filter((client, index, allClients) => allClients.findIndex((client2) => client2._id === client._id) === index),
        "name"
    );

    // this is an ugly hack not to show counter parties with swaps. remove when https://github.com/CaptorAB/nodejs/issues/1045 is fixed
    clients = clients.filter((item) => !["SHB", "DANSKE"].includes(item.name));

    const frontendAssets: PermissionAssetEnum[] = data.me.frontendRole.assets;

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Performance)) {
        menu.push({
            title: "Performance",
            to: "/performance"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Position)) {
        const positionsMenu: any = {};
        positionsMenu.title = "Positions";
        positionsMenu.absolute = true;
        positionsMenu.subMenu = [];
        clients.forEach((client) => {
            positionsMenu.subMenu.push({ title: client.name, to: "/positions/" + client.name.trim().replace(/ /g, "-") });
        });
        menu.push(positionsMenu);
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Transaction)) {
        menu.push({
            title: "Toolbox",
            to: "/transactions",
            subMenu: [
                { title: "Deal Blotter", to: "/dealblotter" },
                { title: "Transactions", to: "/transactions" },
                { title: "Transaction Items", to: "/transactionitems" },
                { title: "Valuations", to: "/valuations" }
            ]
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Accounting)) {
        const subMenu: IMenuItem[] = [
            { title: "Accounting PM", to: "/accountingpm" },
            { title: "T-account charts", to: "/taccountcharts" }
        ];

        if (
            frontendAssets.length === 0 ||
            (frontendAssets.includes(PermissionAssetEnum.Position) && frontendAssets.includes(PermissionAssetEnum.Transaction))
        ) {
            subMenu.push({
                title: "Financial statements",
                to: "/financialstatements"
            });
            subMenu.push({
                title: "Income Statement",
                to: "/incomestatement"
            });
        }

        // new accounting logic, kept apart until it replaces the pages above
        subMenu.push({
            title: "New Accounting",
            subMenu: [
                { title: "Accounting runs", to: "/accountingruns2" },
                { title: "T-account charts", to: "/taccountcharts2" }
            ]
        });

        menu.push({
            title: "Accounting",
            to: "/accountingpm",
            subMenu: subMenu
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.BackOffice)) {
        menu.push({
            title: "FI reporting",
            absolute: true,
            subMenu: [
                { title: "Annual/semiannual reporting", to: "/reporting/annualreports" },
                { title: "Bond classification", to: "/reporting/classification" },
                { title: "Quarterly reporting", to: "/reporting/quarterly" }
            ]
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Instrument)) {
        const subMenu = [{ title: "Instruments", to: "/instruments" }];

        if (userHaveAccessTo("Any", PermissionAssetEnum.Instrument, data.me.roles, PermissionTypeEnum.ReadWrite)) {
            subMenu.push({ title: "New Instrument", to: "/instruments/new" });
            subMenu.push({ title: "Valuation Mappings", to: "/valuationmappings" });
        }

        subMenu.push({ title: "Issuerprograms", to: "/issuerprograms" });
        if (userHaveAccessTo("Any", PermissionAssetEnum.Instrument, data.me.roles, PermissionTypeEnum.ReadWrite)) {
            subMenu.push({ title: "New issuerprogram", to: "/issuerprograms/new" });
        }

        menu.push({
            title: "Instruments",
            to: "/instruments",
            subMenu: subMenu
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Party)) {
        const subMenu = [{ title: "Parties", to: "/parties" }];
        if (userHaveAccessTo("Any", PermissionAssetEnum.Party, data.me.roles, PermissionTypeEnum.ReadWrite)) {
            subMenu.push({ title: "New Party", to: "/parties/new" });
        }
        menu.push({
            title: "Parties",
            to: "/parties",
            subMenu: subMenu
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Nav)) {
        const tradeDate = new Date().toISOString().slice(0, 10);
        let subMenu = [
            ...data.funds.map((fund) => {
                return { title: fund.name.trim().replace(/Captor /g, ""), to: "/calculatenav/" + fund.name.trim().replace(/ /g, "-") };
            })
        ];
        subMenu = sortBy(subMenu, "title");
        subMenu = [
            { title: "TransferAgent Orders", to: "/transferagentorders?tradeDateStart=" + tradeDate },
            { title: "Order Summary", to: "/transferagentorders/summary" },
            ...subMenu
        ];
        subMenu.push({ title: "FO NAV", to: "/fo-nav-beta" });
        subMenu.push({ title: "navs", to: "/navs" });
        menu.push({
            title: "NAV",
            to: "/navs",
            subMenu: subMenu
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Report)) {
        menu.push({
            title: "Reports",
            to: "/reports"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.TimeSeries)) {
        menu.push({
            title: "Timeseries",
            to: "/timeseries"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.PortfolioManager)) {
        menu.push({
            title: "Portfolio Manager",
            to: "/pm"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Limit)) {
        menu.push({
            title: "Limit reports",
            to: "/limitreportv3"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Job)) {
        menu.push({
            title: "JOBS",
            to: "/jobs"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Reconciliation)) {
        menu.push({
            title: "Position reconciliation",
            to: "/positionreconciliation"
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Collateral)) {
        menu.push({ title: "Collateral Management", to: "/reconcile/collateral" });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.BackOffice)) {
        let subMenu = [
            { title: "Agreements", to: "/agreements" },
            { title: "AUM", to: "/aum" },
            { title: "AUM 2", to: "/aum2" },
            { title: "AUM Quarterly", to: "/aum-quarterly" },
            { title: "Bank Accounts Reconciliation", to: "/reconcile/bankaccounts" },
            { title: "Bank Accounts Transactions", to: "/bankaccounttransactions" },
            { title: "Broker Transaction Reconciliation", to: "/reconcile/brokertransactions" },
            { title: "Calendars", to: "/calendars" },
            { title: "Cash balance", to: "/reconcile/cashbalance" },
            { title: "Corporate Actions", to: "/reconcile/corporateactions" },
            { title: "Costs - Monthly", to: "/monthlycosts" },
            { title: "Costs", to: "/backoffice/costs" },
            { title: "Curve Definitions", to: "/curvedefinitions" },
            { title: "Custodian Positions", to: "/reconciliation/custodianpositions" },
            { title: "Customers", to: "/backoffice/customers" },
            { title: "Extensions", to: "/extensions" },
            { title: "Fund Units", to: "/reconcile/fundunits" },
            { title: "Movers", to: "/movers" },
            { title: "Notes ESG", to: "/esgnotes" },
            { title: "Notes", to: "/notes" },
            { title: "Open Payments", to: "/openpayments/payments" },
            { title: "prices", to: "/prices" },
            { title: "quotes", to: "/quotes" },
            { title: "share register", to: "/shareregister" },
            { title: "Swaplab2 compare", to: "/swaplab2/compare" },
            { title: "TA register extracts", to: "/registerextracts" },
            { title: "Units Reconciliation", to: "/reconciliation" },
            { title: "Valuation Mappings", to: "/valuationmappings" },
            { title: "Versions", to: "/versions" },
            { title: "Account performance", to: "/accountperformance" }
        ];
        subMenu = sortBy(subMenu, "title");

        menu.push({
            title: "Back office",
            subMenu: subMenu
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Playground)) {
        if (login.token) {
            menu.push({
                title: "GraphiQL",
                absolute: true,
                to: REACT_APP_API_URI + "/graphiql"
            });
        }
    }

    if (userHaveAccessTo("Any", PermissionAssetEnum.Transaction, data.me.roles, PermissionTypeEnum.ReadWrite)) {
        menu.push({
            title: "Change Password",
            to: "/changepassword"
        });
    }

    if (userHaveAccessTo("Any", PermissionAssetEnum.Admin, data.me.roles, PermissionTypeEnum.Read)) {
        const subMenu = [
            { title: "Users", to: "/admin/users" },
            { title: "Redis", to: "/admin/redis" }
        ];

        if (userHaveAccessTo("Any", PermissionAssetEnum.Admin, data.me.roles, PermissionTypeEnum.ReadWrite)) {
            subMenu.push({ title: "New User", to: "/admin/users/new" });
        }
        subMenu.push({ title: "Roles", to: "/admin/roles" });

        if (userHaveAccessTo("Any", PermissionAssetEnum.Admin, data.me.roles, PermissionTypeEnum.ReadWrite)) {
            subMenu.push({ title: "New Role", to: "/admin/roles/new" });
        }

        menu.push({
            title: "Admin",
            absolute: true,
            subMenu
        });
    }

    if (frontendAssets.length === 0 || frontendAssets.includes(PermissionAssetEnum.Esg)) {
        const subMenu = [{ title: "Screens", to: "/screens" }];

        if (userHaveAccessTo("Any", PermissionAssetEnum.Esg, data.me.roles, PermissionTypeEnum.ReadWrite)) {
            subMenu.push({ title: "New screen", to: "/screens/new" });
        }
        subMenu.push({ title: "EsgDataProviders", to: "/parties?types=EsgDataProvider" });

        if (userHaveAccessTo("Any", PermissionAssetEnum.Esg, data.me.roles, PermissionTypeEnum.Read)) {
            subMenu.push({ title: "Fund data", to: "/esg" });
        }

        menu.push({
            title: "ESG",
            absolute: true,
            subMenu
        });
    }

    if (login.loggedIn === false) {
        return null;
    }

    menu = sortBy(menu, "title");
    menu = [].concat(...[{ title: "Home", to: "/" }], ...menu);

    return (
        <Fragment>
            {isOpen ? <div aria-hidden="true" className="fixed inset-0 z-[998] bg-slate-900/40" onClick={closeSideNav} /> : null}
            <aside
                id="sideNav"
                aria-label="Main navigation"
                className={`fixed inset-y-0 left-0 z-[999] flex w-[300px] flex-col bg-sky-900 text-white transition-transform duration-300 ease-in-out ${
                    isOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
                    <div className="min-w-0">
                        <p className="!mb-0 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">Welcome</p>
                        <p className="!mb-0 truncate text-base font-semibold !text-white">{data && data.me ? data.me.name : ""}</p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close menu"
                        onClick={closeSideNav}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md !border-0 bg-transparent p-0 text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <nav
                    id="mainNav"
                    className="flex-1 overflow-y-auto px-2 py-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-white/20"
                >
                    <ul id="menu-main-nav" className="mb-0 list-none space-y-0.5 p-0">
                        {menu.map((d, i) => (
                            <MenuLink key={i} value={d} onClose={closeSideNav} />
                        ))}
                    </ul>
                </nav>
                <div className="border-t border-white/10 px-2 py-3">
                    <Link
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-[15px] font-medium !text-white no-underline transition-colors hover:bg-white/10 hover:no-underline"
                        to="/"
                        onClick={async () => {
                            const _response = await request({
                                method: "DELETE",
                                url: REACT_APP_AUTH_URI + "/set",
                                headers: {
                                    Accept: "application/json; charset=utf-8",
                                    "Content-Type": "application/json",
                                    authorization: `Bearer ${login.token}` // this should not be needed
                                }
                            })
                                .catch((error) => {
                                    console.log(error);
                                })
                                .finally(() => {
                                    //console.log("unset cookies done1");
                                });
                            if (REACT_APP_DB_NAME === "prod") {
                                axios
                                    .delete(REACT_APP_NODEJOBAPI_URI + "/set", {
                                        withCredentials: true,
                                        headers: {
                                            "Content-Type": "application/json",
                                            authorization: `Bearer ${login.token}` // this should not be needed
                                        }
                                    })
                                    .catch(function (error) {
                                        console.log(error);
                                    });
                                axios
                                    .post(
                                        REACT_APP_PYJOBAPI_URI + "/unset",
                                        {},
                                        {
                                            withCredentials: true,
                                            headers: {
                                                "Content-Type": "application/json",
                                                authorization: `Bearer ${login.token}` // this should not be needed
                                            }
                                        }
                                    )
                                    .catch(function (error) {
                                        console.log(error);
                                    });
                            }
                            //console.log(_response);
                            setLogin(getDefaultLoginValue());
                        }}
                    >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                    </Link>
                    <div className="mt-2 space-y-1 px-3 text-xs text-white/50">
                        <Link className="!text-white/70 no-underline hover:!text-white hover:underline" to="/termsofservice">
                            Terms of service
                        </Link>
                        {browserName === "Edge" ? (
                            <p className="!mb-0 text-amber-300/90">
                                We noticed 5-10 times slower response in Edge compared to Chrome browser.
                            </p>
                        ) : null}
                        <p className="!mb-0 truncate" title={`Version ${REACT_APP_GIT_HASH}`}>
                            Version {REACT_APP_GIT_HASH} · {VITE_DATE}
                        </p>
                    </div>
                </div>
            </aside>
        </Fragment>
    );
};
